import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { CurrentDashboardProfileContext, DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { combineLatest, filter, map, merge, Observable, Subject, switchMap } from 'rxjs';
import { BillingDetailsService } from '../domain/data-access/billing-details.service';
import { BillingDetails } from '../domain/entities/billing-details';
import { UpdateBillingContactDetailsComponent } from '../orc-update-billing-contact-details/update-billing-contact-details.component';

@Component({
  selector: 'rma-billing-contact-details',
  templateUrl: './billing-contact-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, TranslocoModule, MatButtonModule],
  providers: [provideTranslocoScope('subscriptions')],
  host: {
    class: 'block',
  },
})
export class BillingContactDetailsComponent {
  @Input()
  public currentContext?: CurrentDashboardProfileContext;

  public error$ = new Subject<string | undefined>();
  public currentBillingEmail$: Observable<string>;

  private billingDetails$: Observable<BillingDetails>;
  private profileCode: Observable<string>;
  private email: Observable<string>;
  private isAgency: Observable<boolean>;
  private updatedEmail = new Subject<string>();

  constructor(
    private readonly billingDetailsService: BillingDetailsService,
    private readonly dialog: MatDialog,
    private readonly profileContextService: DashboardProfileContextService,
  ) {
    this.profileCode = this.profileContextService.profileCode$;

    this.email = this.profileContextService.profileEmail$;
    this.isAgency = this.profileContextService.isAgency$;

    this.billingDetails$ = combineLatest([this.isAgency, this.profileCode]).pipe(
      filter(([isAgency, _]) => isAgency),
      switchMap(([_, code]) => this.billingDetailsService.getAgencyDetails(code)),
    );

    const billingEmail$ = combineLatest([this.billingDetails$, this.email]).pipe(
      map(([billingEmail, profileEmail]) => billingEmail.billingEmailAddress ?? profileEmail),
    );

    this.currentBillingEmail$ = merge(billingEmail$, this.updatedEmail);
  }

  public updateBillingDetails(email: string) {
    this.dialog
      .open<UpdateBillingContactDetailsComponent, string>(UpdateBillingContactDetailsComponent, { data: email })
      .afterClosed()
      .subscribe((e) => {
        if (e) {
          this.updatedEmail.next(e);
        }
      });
  }
}
